// Отчёты руководителя: подписи и правила показа для зеркала exec-панели (pex_*).
//
// ЧЕГО В ЭТОМ РАЗДЕЛЕ НЕТ И ПОЧЕМУ — ЧИТАТЬ ПЕРВЫМ.
// Кассы и ФОТ здесь нет вовсе. В зеркале они лежат, и это не мелочь: в `pex_fin_ops`
// 4 462 выплаты людям на ≈103,6 млн ₽ и 257 ФИО (замер 28.07.2026). Но решения владельца
// о том, кто это может видеть, пока нет,
// поэтому ядро их не читает (см. `pex_api.py`), а раздел про это ГОВОРИТ, а не молчит.
// Следствие, которое обязано быть на экране: прибыль подразделения здесь не считается.
// Формула панели — «разница − ФОТ − расходы − наличные», и без двух последних слагаемых
// получилась бы не прибыль, а разница минус счета поставщикам.
//
// ГЛАВНОЕ ПРАВИЛО ФАЙЛА. «Мои люди» и «моя зона ответственности» — ДВА РАЗНЫХ ИЗМЕРЕНИЯ,
// и здесь нет ни одной функции, которая сводила бы их в одно число:
//   • «по людям» — оргструктура (UF_DEPARTMENT + эффективный руководитель отдела);
//   • «по зоне»  — бизнес-юнит в сделке и в платеже.
// Связи между ними не существует ни в Bitrix, ни в панели: `pex_dept_unit` пуст (0 строк).
// Живая проверка 26.07.2026: у Максима Орлова через ручную заплатку 5 отделов и 26 человек
// и НОЛЬ неоплаченных платежей на них, при дебиторке юнита «Санкт-Петербург» 44,5 млн ₽.
// Обе цифры настоящие. Складывать нельзя — деньги по Петербургу висят на координаторах
// чужого отдела.
//
// Своих формул по деньгам здесь тоже нет: раздел показывает ГОТОВЫЕ суммы из снимков
// панели и суммы платежей из ядра. Долг и просрочку считает lib/payments.ts, и второго
// определения долга в CRM появляться не должно.

// ─────────────────────────── виды проверок «косяков» ───────────────────────────
// Коды приходят из панели (`pex_kosyak_seen.chk`), человеческих названий в данных нет —
// они жили в коде панели (`ЗНАНИЕ_из_exec_панели.md` §3.7). Незнакомый код показываем
// КАК ЕСТЬ: заведут десятый детектор — он появится на экране, а не пропадёт.

export interface ВидПроверки {
  ярлык: string
  /** Что именно не так со сделкой. Без этого код «avr» ничего не сообщает. */
  смысл: string
  /** Чинится в данных/процессе, а не звонком клиенту. */
  процесс?: boolean
}

const ВИДЫ: Record<string, ВидПроверки> = {
  avr: {
    ярлык: 'АВР есть, счёта нет',
    смысл: 'работы сданы (акт подписан), а счёт клиенту не выставлен — деньги не начали идти',
  },
  paid_deal_unpaid_invoice: {
    ярлык: 'выиграна, счета не оплачены',
    смысл: 'сделка закрыта успехом, а счета клиенту в неоплаченных стадиях',
  },
  paid_deal_unpaid_payment: {
    ярлык: 'выиграна, платежи не получены',
    смысл: 'сделка закрыта успехом, а платежи по ней не оплачены',
  },
  stuck: {
    ярлык: 'без движения',
    смысл: 'открытая сделка, которую давно не трогали',
  },
  lost_shipped: {
    ярлык: 'проиграна, но отгружена',
    смысл: 'сделка проиграна, а накладная, МойСклад или дата отгрузки заполнены — '
      + 'товар ушёл к клиенту по проигранной сделке',
    процесс: true,
  },
  costs_no_invoice: {
    ярлык: 'расходы есть, счёта нет',
    смысл: 'по сделке есть расходы, а счёт клиенту не выставлен',
  },
  lost_with_costs: {
    ярлык: 'проиграна, закупки оплачены',
    смысл: 'сделка проиграна, а поставщикам уже заплатили — это прямой убыток',
  },
  won_zero: {
    ярлык: 'выиграна с нулевой суммой',
    смысл: 'сделка закрыта успехом при сумме не больше рубля — скорее всего сумму не внесли',
    процесс: true,
  },
  old_supplier_invoices: {
    ярлык: 'старые счета поставщикам',
    смысл: 'счета поставщикам согласованы, но не оплачены дольше 150 дней',
  },
}

/** Подпись вида проверки. Незнакомый код возвращается как есть — прятать его нельзя. */
export function видПроверки(chk?: string | null): ВидПроверки {
  const код = String(chk ?? '').trim()
  if (!код) return { ярлык: 'вид не указан', смысл: 'код проверки в данных пуст' }
  return ВИДЫ[код] ?? {
    ярлык: код,
    смысл: 'этого вида проверки мы не знаем: он появился в панели позже. Показан как есть',
  }
}

/** Известен ли нам код — экран помечает незнакомые, а не делает вид, что всё понял. */
export const видИзвестен = (chk?: string | null): boolean =>
  Object.prototype.hasOwnProperty.call(ВИДЫ, String(chk ?? ''))

// ─────────────────────────── возраст «косяка» ───────────────────────────

/**
 * Подпись возраста ошибки. Ровно то место, где экран обязан сказать «не меньше».
 *
 * Почему. Таблицу `pex_kosyak_seen` начали вести 07.07.2026, и 3 187 записей из 3 365
 * помечены именно этой датой: сколько ошибка висела ДО того, не знает никто. Написать
 * «висит 19 дней» про такую запись — соврать в самую полезную сторону (руководитель решит,
 * что проблема свежая). Поэтому у обрезанных записей впереди «≥».
 */
export function возрастКосяка(дней?: number | null, обрезан?: boolean): string {
  if (дней === null || дней === undefined) return '—'
  const слово = склонение(дней, 'день', 'дня', 'дней')
  return `${обрезан ? '≥ ' : ''}${дней} ${слово}`
}

/** Русское склонение после числа: без него экран пишет «1 дней» и «21 дней». */
export function склонение(n: number, one: string, few: string, many: string): string {
  const a = Math.abs(n) % 100
  const b = a % 10
  if (a > 10 && a < 20) return many
  if (b === 1) return one
  if (b >= 2 && b <= 4) return few
  return many
}

// ─────────────────────────── динамика дебиторки ───────────────────────────

export type Направление = 'вверх' | 'вниз' | 'ровно' | 'неизвестно'

/**
 * Куда двинулся долг. `null` — двинулся неизвестно куда, и это НЕ «не изменился».
 *
 * Порог в рублях, а не в процентах: у юнита с долгом 83 160 ₽ изменение на 500 ₽ — это
 * 0,6 %, но это же и есть «ничего не произошло». Порог показан на экране рядом.
 */
export const ПОРОГ_РОВНО = 1000

export function направление(дельта?: number | null): Направление {
  if (дельта === null || дельта === undefined) return 'неизвестно'
  if (Math.abs(дельта) < ПОРОГ_РОВНО) return 'ровно'
  return дельта > 0 ? 'вверх' : 'вниз'
}

/**
 * Класс значка для дельты долга. Рост долга — плохо, снижение — хорошо; у просрочки
 * знак тот же. `work` для «неизвестно» намеренно: это не нейтральное «ок», а «не знаем».
 */
export function классДельты(дельта?: number | null): string {
  const н = направление(дельта)
  if (н === 'вверх') return 'badge lose'
  if (н === 'вниз') return 'badge won'
  return 'badge work'
}

/** «+13,4 млн» / «−2,7 млн» / «—». Знак обязателен: без него дельта читается как остаток. */
export function дельтаКратко(дельта?: number | null): string {
  if (дельта === null || дельта === undefined) return '—'
  const знак = дельта > 0 ? '+' : дельта < 0 ? '−' : ''
  return знак + млн(Math.abs(дельта))
}

/** Крупные суммы миллионами: в таблице на 21 строку полные рубли не читаются. */
export function млн(v?: number | null): string {
  if (v === null || v === undefined) return '—'
  const a = Math.abs(v)
  if (a >= 1_000_000) return `${(v / 1_000_000).toLocaleString('ru-RU', { maximumFractionDigits: 1 })} млн`
  if (a >= 1_000) return `${(v / 1_000).toLocaleString('ru-RU', { maximumFractionDigits: 0 })} тыс`
  return v.toLocaleString('ru-RU', { maximumFractionDigits: 0 })
}

/**
 * Полнота истории по юниту: «данные за 9 дней из 20».
 *
 * Панель пишет строку снимка ТОЛЬКО тем юнитам, у которых в этот день были платежи.
 * Значит пропущенный день — это не нулевая дебиторка, а отсутствие строки, и дельта у
 * такого юнита посчитана между крайними имеющимися днями, а не между границами окна.
 */
export function полнотаИстории(дней?: number | null, окно?: number | null): string {
  const d = дней ?? 0
  const w = окно ?? 0
  if (!w) return `${d} ${склонение(d, 'день', 'дня', 'дней')}`
  if (d >= w) return `все ${w} ${склонение(w, 'день', 'дня', 'дней')}`
  return `${d} ${склонение(d, 'день', 'дня', 'дней')} из ${w}`
}

// ─────────────────────────── переносы срока оплаты ───────────────────────────

/**
 * Насколько тревожен платёж по числу переносов.
 *
 * Порог «больше двух» взят не из головы: в exec-панели ровно он отправлял платёж в
 * «3-й эшелон» — то есть в список «это надо ЧИНИТЬ (данные или процесс), а не звонить
 * клиенту» (`ЗНАНИЕ_из_exec_панели.md` §3.3). Здесь тот же порог и то же значение.
 */
export const ЭШЕЛОН_ПЕРЕНОСОВ = 2

export function классПереносов(переносов?: number | null): string {
  const n = переносов ?? 0
  if (n > ЭШЕЛОН_ПЕРЕНОСОВ) return 'badge lose'
  if (n === ЭШЕЛОН_ПЕРЕНОСОВ) return 'badge work'
  return 'badge'
}

/** «перенесли 5 раз» — с русским склонением и без «5 раз(а)». */
export const переносовСловами = (n?: number | null): string =>
  `${n ?? 0} ${склонение(n ?? 0, 'раз', 'раза', 'раз')}`

/**
 * Подпись «сдвинули после того, как срок наступил».
 *
 * Это самый острый признак во всём разделе и главный ответ на «почему долг не гаснет»:
 * срок не уточнили заранее, а отодвинули, когда он уже прошёл. На живых данных
 * таких переносов 385 из 478, у 344 платежей из 404 на 135,1 млн ₽ (замер 28.07.2026).
 */
export function поздниеПереносы(поздних?: number | null, всего?: number | null): string {
  const p = поздних ?? 0
  if (!p) return 'все переносы — до наступления срока'
  const в = всего ?? p
  return `${p} из ${в} — уже после наступления срока`
}

// ─────────────────────────── эффективный руководитель ───────────────────────────

/**
 * Откуда взялся руководитель отдела. Показывать обязательно: «bitrix» — это настоящие
 * данные, «override» — ручная заплатка exec-панели, которая в Bitrix НЕ пишется и в ядре
 * взяться не может. Правильное лечение — заполнить UF_HEAD в Bitrix, и тогда заплатка
 * становится ненужной; на экране это написано словами.
 */
export function источникРуководителя(source?: string | null): { ярлык: string; смысл: string } {
  if (source === 'bitrix') {
    return {
      ярлык: 'из Bitrix',
      смысл: 'поле «Руководитель» (UF_HEAD) у отдела заполнено — это настоящие данные',
    }
  }
  if (source === 'override') {
    return {
      ярлык: 'заплатка панели',
      смысл: 'UF_HEAD в Bitrix пуст, руководителя вписали руками в exec-панели. '
        + 'В Bitrix эта запись не уходит: правильное лечение — заполнить UF_HEAD там',
    }
  }
  return {
    ярлык: 'не указан',
    смысл: 'руководителя у отдела нет ни в Bitrix, ни в заплатке панели. Отчёт «по моим '
      + 'людям» для этого отдела пуст у ВСЕХ сотрудников — это не про конкретного человека',
  }
}

// ─────────────────────────── свежесть ───────────────────────────

/**
 * Возраст отчёта складывается из ДВУХ разных чисел, и путать их нельзя:
 *   • когда ядро последний раз читало панель (раз в 15 минут журнал, раз в час снимки);
 *   • какой ДЕНЬ лежит в самих данных — снимок дебиторки суточный.
 * «Синк прошёл минуту назад» не означает «данные на сейчас», и на экране стоят оба.
 */
export function минутНазад(момент?: string | null, сейчас?: string | null): number | null {
  if (!момент) return null
  const t = new Date(момент).getTime()
  if (Number.isNaN(t)) return null
  const n = сейчас ? new Date(сейчас).getTime() : Date.now()
  if (Number.isNaN(n)) return null
  return Math.max(0, Math.round((n - t) / 60_000))
}

/** «14 минут назад» / «—». Часы для суток и больше — иначе получается «1435 минут». */
export function давность(минут?: number | null): string {
  if (минут === null || минут === undefined) return '—'
  if (минут < 1) return 'меньше минуты назад'
  if (минут < 90) return `${минут} ${склонение(минут, 'минуту', 'минуты', 'минут')} назад`
  const часов = Math.round(минут / 60)
  if (часов < 36) return `${часов} ${склонение(часов, 'час', 'часа', 'часов')} назад`
  const дней = Math.round(часов / 24)
  return `${дней} ${склонение(дней, 'день', 'дня', 'дней')} назад`
}

/** Сколько дней между двумя днями ГГГГ-ММ-ДД. Не даты → null. */
export function дней(от?: string | null, до?: string | null): number | null {
  if (!от || !до) return null
  const a = Date.parse(`${от.slice(0, 10)}T12:00:00Z`)
  const b = Date.parse(`${до.slice(0, 10)}T12:00:00Z`)
  if (Number.isNaN(a) || Number.isNaN(b)) return null
  return Math.round((b - a) / 86_400_000)
}

// ─────────────────────────── сборка строки запроса ───────────────────────────
// Пустые значения не отправляем: ядро игнорирует неизвестные параметры, но пустой
// `chk=` в ключе кэша плодил бы разные ключи на одинаковый запрос.

export type ФильтрыPex = Record<string, string | number | boolean | undefined | null>

export function строкаЗапроса(ф: ФильтрыPex): string {
  const p = new URLSearchParams()
  for (const [k, v] of Object.entries(ф)) {
    if (v === undefined || v === null || v === '' || v === false) continue
    p.set(k, v === true ? '1' : String(v))
  }
  const s = p.toString()
  return s ? `?${s}` : ''
}
